
import React, { useEffect, useState } from 'react';
import { supabase } from '../services/supabase';
import { UserProfile } from '../types';
import Swal from 'sweetalert2';

interface ProfileProps {
  onHeightChange?: (height: number) => void;
}

const Profile: React.FC<ProfileProps> = ({ onHeightChange }) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  const [editForm, setEditForm] = useState({
    full_name: '',
    birth_date: '',
    height: 0
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (data) {
          setProfile(data);
          setEditForm({
            full_name: data.full_name || '',
            birth_date: data.birth_date || '',
            height: data.height || 0
          });
          if (onHeightChange && data.height) onHeightChange(data.height);
        }
      }
    } catch (err) {
      console.error("Error fetching profile:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      setSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          ...editForm,
          gender: profile?.gender || 'ชาย' 
        });

      if (error) throw error;
      
      setProfile({ ...profile!, ...editForm });
      setIsEditing(false);

      if (onHeightChange) onHeightChange(editForm.height);
      
      Swal.fire({
        title: 'บันทึกสำเร็จ',
        text: 'ข้อมูลโปรไฟล์ของคุณถูกอัปเดตแล้ว',
        icon: 'success',
        confirmButtonColor: '#2563eb'
      });
    } catch (error: any) {
      Swal.fire({
        title: 'เกิดข้อผิดพลาด',
        text: error.message,
        icon: 'error',
        confirmButtonColor: '#2563eb'
      });
    } finally {
      setSaving(false);
    }
  };

  const cancelEditing = () => {
    if (profile) {
      setEditForm({
        full_name: profile.full_name || '',
        birth_date: profile.birth_date || '',
        height: profile.height || 0
      });
    }
    setIsEditing(false);
  };

  const handleLogout = async () => {
    const result = await Swal.fire({
      title: 'ต้องการออกจากระบบ?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'ออกจากระบบ',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#94a3b8'
    });

    if (result.isConfirmed) {
      await supabase.auth.signOut();
    }
  };

  const handleConfirmDelete = async () => {
    const result = await Swal.fire({
      title: 'ยืนยันการลบบัญชี?',
      text: 'ข้อมูลทั้งหมดของคุณจะหายไปถาวร',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยืนยันการลบ',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#94a3b8'
    });

    if (result.isConfirmed) {
      Swal.fire({
        title: 'ส่งคำขอแล้ว',
        text: 'ทีมงานจะดำเนินการตรวจสอบและลบข้อมูลภายใน 24 ชม.',
        icon: 'info',
        confirmButtonColor: '#2563eb'
      });
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center p-20 space-y-4">
      <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-slate-400 font-bold text-sm">กำลังโหลดข้อมูล...</p>
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="bg-white dark:bg-slate-900 p-8 rounded-[40px] shadow-sm border border-slate-100 dark:border-slate-800 text-center relative overflow-hidden transition-colors">
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-blue-50 dark:from-blue-900/10 to-transparent"></div>
        <div className="relative z-10">
          <div className="w-24 h-24 bg-white dark:bg-slate-800 rounded-full mx-auto mb-4 flex items-center justify-center text-blue-600 dark:text-blue-400 text-3xl font-bold border-4 border-blue-50 dark:border-blue-900/20 shadow-inner">
            {profile?.full_name ? profile.full_name.charAt(0) : 'U'}
          </div>
          <h2 className="text-2xl font-black text-slate-800 dark:text-slate-100">
            {profile?.full_name || 'สมาชิกใหม่'}
          </h2>
          <p className="text-blue-500 dark:text-blue-400 text-xs font-bold mt-1 uppercase tracking-[0.2em]">
            Digital Health ID: {profile?.id.slice(0, 8).toUpperCase() || 'Pending'}
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 p-6 rounded-[32px] shadow-sm border border-slate-100 dark:border-slate-800 space-y-6 transition-colors">
        <div className="flex justify-between items-center">
          <h3 className="font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2 uppercase text-xs tracking-wider">
            <svg className="w-4 h-4 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            ข้อมูลพื้นฐานด้านสุขภาพ
          </h3>
          {!isEditing ? (
            <button onClick={() => setIsEditing(true)} className="flex items-center gap-1.5 text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-4 py-2 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-all hover:scale-105">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
              แก้ไขข้อมูล
            </button>
          ) : (
            <button onClick={cancelEditing} className="text-xs font-bold text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
              ยกเลิกการแก้ไข
            </button>
          )}
        </div>

        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">ชื่อ-นามสกุล</label>
            {isEditing ? (
              <input type="text" value={editForm.full_name} onChange={(e) => setEditForm({...editForm, full_name: e.target.value})} className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm text-slate-700 dark:text-slate-200 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-medium" placeholder="กรอกชื่อและนามสกุล" />
            ) : (
              <div className="p-4 bg-slate-50 dark:bg-slate-800 border border-transparent rounded-2xl text-sm font-bold text-slate-700 dark:text-slate-300 transition-colors">{profile?.full_name || 'ยังไม่ระบุ'}</div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">วัน/เดือน/ปี เกิด</label>
              {isEditing ? (
                <input type="date" value={editForm.birth_date} onChange={(e) => setEditForm({...editForm, birth_date: e.target.value})} className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm text-slate-700 dark:text-slate-200 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-medium" />
              ) : (
                <div className="p-4 bg-slate-50 dark:bg-slate-800 border border-transparent rounded-2xl text-sm font-bold text-slate-700 dark:text-slate-300 transition-colors">{profile?.birth_date ? new Date(profile.birth_date).toLocaleDateString('th-TH') : 'ยังไม่ระบุ'}</div>
              )}
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">ส่วนสูง (ซม.)</label>
              {isEditing ? (
                <input type="number" value={editForm.height} onChange={(e) => setEditForm({...editForm, height: parseFloat(e.target.value)})} className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm text-slate-700 dark:text-slate-200 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-medium" placeholder="เช่น 170" />
              ) : (
                <div className="p-4 bg-slate-50 dark:bg-slate-800 border border-transparent rounded-2xl text-sm font-bold text-slate-700 dark:text-slate-300 transition-colors">{profile?.height ? `${profile.height} ซม.` : 'ยังไม่ระบุ'}</div>
              )}
            </div>
          </div>
        </div>

        {isEditing && (
          <div className="pt-2 animate-in slide-in-from-bottom-2 duration-300">
            <button onClick={handleUpdateProfile} disabled={saving} className="w-full py-4 bg-blue-600 dark:bg-blue-500 text-white font-black rounded-2xl shadow-xl shadow-blue-100 dark:shadow-none hover:bg-blue-700 dark:hover:bg-blue-600 active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
              {saving ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : 'บันทึกการเปลี่ยนแปลง'}
            </button>
          </div>
        )}
      </div>

      <div className="space-y-3 pt-4">
        <button onClick={handleLogout} className="w-full py-4 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 font-bold rounded-2xl border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 active:scale-95 transition-all shadow-sm flex items-center justify-center gap-2"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>ออกจากระบบ</button>
        <button onClick={handleConfirmDelete} className="w-full py-4 text-red-500 dark:text-red-400 font-bold hover:bg-red-50 dark:hover:bg-red-900/10 rounded-2xl transition-all flex items-center justify-center gap-2"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>ลบบัญชีและข้อมูลสุขภาพ</button>
      </div>
    </div>
  );
};

export default Profile;
